import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DONATE_URL } from "@/lib/links";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Flag } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { api, formatApiErrorDetail } from "@/lib/api";

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming",
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  state: "",
  city: "",
  occupation: "",
  experience: "",
  reason: "",
  estimated_supporters: "",
  availability: "",
  background_check_ok: false,
};

const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2";
const inputCls = "w-full border border-slate-300 bg-white px-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] focus:ring-1 focus:ring-[#D90429] transition-colors duration-200";

const Field = ({ label, name, value, onChange, type = "text", required = true, placeholder = "" }) => (
  <div>
    <label className={labelCls}>{label}{required && <span className="text-[#D90429]"> *</span>}</label>
    <input data-testid={`field-${name}`} type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder} className={inputCls} />
  </div>
);

const Area = ({ label, name, value, onChange, required = true, placeholder = "" }) => (
  <div>
    <label className={labelCls}>{label}{required && <span className="text-[#D90429]"> *</span>}</label>
    <textarea data-testid={`field-${name}`} name={name} value={value} onChange={onChange} required={required} rows={4} placeholder={placeholder} className={`${inputCls} resize-none`} />
  </div>
);

export default function StartChapter() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ ...initial, state: params.get("state") || "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.state) { toast.error("Please select your state."); return; }
    setLoading(true);
    try {
      await api.post("/chapter-applications", form);
      setDone(true);
      toast.success("Chapter request submitted! Our team will be in touch.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <PageHero
        kicker="Lead The Front Line"
        title={<>Start A <span className="text-[#D90429]">Chapter</span></>}
        subtitle="Don't see a chapter near you? Build one. Chapter leaders organize local patriots, coordinate training, and stand ready to protect their communities. Tell us about yourself and our chapter development team will guide you through launch."
      />
      <section className="pt-16 pb-24 bg-muted min-h-screen">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">

          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              data-testid="chapter-application-success"
              className="mt-12 bg-white border border-slate-200 p-10 text-center"
            >
              <Flag className="w-16 h-16 text-[#D90429] mx-auto mb-6" />
              <h2 className="font-display text-3xl uppercase">Request Received, Patriot</h2>
              <p className="text-slate-600 mt-3 max-w-md mx-auto">
                Thank you for stepping up to lead. Our chapter development team will review your
                request and reach out within a few days to discuss next steps.
              </p>
              <button
                data-testid="chapter-go-donate-button"
                onClick={() => window.open(DONATE_URL, "_blank", "noopener,noreferrer")}
                className="mt-8 inline-flex items-center gap-2 bg-[#D90429] text-white px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95"
              >
                Support The Mission <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="chapter-application-form" className="mt-12 bg-white border border-slate-200 p-6 sm:p-10">
              <div className="mb-10">
                <h3 className="font-display text-2xl uppercase mb-6 pb-3 border-b-2 border-[#0A1128]">Your Info</h3>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} placeholder="(FIRST) (LAST)" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} />
                  <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                  <Field label="Occupation" name="occupation" value={form.occupation} onChange={onChange} required={false} placeholder="(optional)" />
                </div>
              </div>

              <div className="mb-10">
                <h3 className="font-display text-2xl uppercase mb-6 pb-3 border-b-2 border-[#0A1128]">Chapter Location</h3>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>State<span className="text-[#D90429]"> *</span></label>
                    <select data-testid="field-state" name="state" value={form.state} onChange={onChange} required className={inputCls}>
                      <option value="">Select your state</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <Field label="City / Region" name="city" value={form.city} onChange={onChange} placeholder="e.g. Austin / Central Texas" />
                </div>
              </div>

              <div className="mb-10">
                <h3 className="font-display text-2xl uppercase mb-6 pb-3 border-b-2 border-[#0A1128]">Leadership & Motivation</h3>
                <div className="grid gap-6">
                  <Area label="Prior leadership / military / LEO experience" name="experience" value={form.experience} onChange={onChange} required={false} placeholder="Military, law enforcement, EMS, organizing, volunteer leadership… (optional)" />
                  <Area label="Why do you want to lead a chapter?" name="reason" value={form.reason} onChange={onChange} placeholder="Tell us what drives you to organize your community." />
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Estimated local supporters" name="estimated_supporters" value={form.estimated_supporters} onChange={onChange} required={false} placeholder="e.g. 10–20" />
                    <Field label="Availability / time commitment" name="availability" value={form.availability} onChange={onChange} required={false} placeholder="e.g. evenings & weekends" />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input data-testid="field-background_check_ok" type="checkbox" name="background_check_ok" checked={form.background_check_ok} onChange={onChange} className="mt-1 w-4 h-4 accent-[#D90429]" />
                    <span className="text-sm text-slate-600">I understand chapter leaders may be asked to undergo a background check and I am willing to do so.</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-chapter-application-button"
                className="w-full bg-[#D90429] text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99] disabled:opacity-60"
              >
                {loading ? "Submitting…" : "Submit Chapter Request"}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
